import { Button, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useCurrencySign } from "web_myplann/Dashboard/Hooks/useCurrencySign";
import type { DashboardType } from "web_myplann/Dashboard/Types/DashboardType";
import { DASHBOARD_CARD_TOOLTIP_OFFSET } from "web_myplann/Dashboard/Utils/dashboardLayout";
import { getDashboardValueState } from "web_myplann/Dashboard/Utils/getDashboardValueState";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyWidgetTooltip } from "web_myplann/Shared/Components/PropertyWidgetTooltip";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { DashboardCardValue } from "./DashboardCardValue";
import { EvaluationChip } from "./EvaluationChip";

type Props = {
	dashboardData: DashboardType;
	isDashboardDetail?: boolean;
	navigateToDashboardDetail?: () => void;
	onTooltipToggle?: (open: boolean) => void;
};

export const DashboardCardContent: FC<Props> = ({
	dashboardData,
	isDashboardDetail,
	navigateToDashboardDetail,
	onTooltipToggle,
}) => {
	const { t } = useResource();
	const { palette, spacingTokens } = useTheme();
	const currencySign = useCurrencySign(dashboardData.currency);

	const { isMissingValueWithItems, evaluation } =
		getDashboardValueState(dashboardData);
	const { netValue, overviewType } = dashboardData;

	const showNetValue =
		netValue !== undefined &&
		overviewType === PortfolioReportingOverviewTypeEnum.Total;

	return (
		<Stack
			width="100%"
			height="100%"
			alignItems="center"
			justifyContent={isDashboardDetail ? "center" : "flex-start"}
			pt={isDashboardDetail ? 0 : "26px"}
		>
			<Stack
				alignItems="center"
				justifyContent="flex-start"
				gap={spacingTokens.xxl}
			>
				<Stack
					alignItems="center"
					justifyContent="flex-start"
					gap={spacingTokens.sm}
					height="92px"
				>
					<Stack alignItems="center">
						{!isDashboardDetail && (
							<Stack direction="row" alignItems="center" gap={spacingTokens.xs}>
								<Typography
									variant="deprecatedBodyStrong"
									color={palette.darkmodeText.white}
									lineHeight={"20px"}
									zIndex={1}
								>
									{dashboardData.type}
								</Typography>
								<PropertyWidgetTooltip
									title={t(Resources.Dashboard.TitleCard.tooltip)}
									onToggle={onTooltipToggle}
									offset={DASHBOARD_CARD_TOOLTIP_OFFSET}
								/>
							</Stack>
						)}
						<DashboardCardValue
							dashboardData={dashboardData}
							isMissingValueWithItems={isMissingValueWithItems}
						/>
					</Stack>
					{evaluation !== undefined && (
						<EvaluationChip evaluation={evaluation} />
					)}
					{showNetValue && (
						<Stack direction="row" alignItems="center" zIndex={1}>
							<Typography
								variant="deprecatedBody1"
								color={palette.darkmodeText.white}
								tabular
							>
								{t(Resources.Dashboard.TitleCard.netValue, {
									value: moneyFormatter(
										Math.round(netValue),
										currencySign,
										`0 ${currencySign}`,
									),
								})}
							</Typography>
							<PropertyWidgetTooltip
								title={t(Resources.Dashboard.TitleCard.netValueTooltip)}
								onToggle={onTooltipToggle}
								size={14}
								offset={DASHBOARD_CARD_TOOLTIP_OFFSET}
							/>
						</Stack>
					)}
				</Stack>

				{navigateToDashboardDetail && (
					<Button
						variant="deprecatedOnGradient"
						sx={{ color: dashboardData.gradientFrom }}
						onClick={navigateToDashboardDetail}
					>
						{t(Resources.Dashboard.TitleCard.title)}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
